import type { Provenance } from '@/components/data-truth/provenance'
import type { Status } from '@/components/ui/status-chip'

// ---------------------------------------------------------------------------
// /admin/v2 typed shapes — mirror api/admin.v2.yaml exactly (ADR-0002 D6).
// ---------------------------------------------------------------------------

/** Provenance for an aggregated payload (ADR-0002 D4, honest-source rule). */
export interface Source {
  kind: string
  observed_at: string
  stale: boolean
  /** Optional honesty note explaining a coarse/absent signal (envelope.go
   * Source.note, omitempty) — e.g. why a signal is a gap or errors aren't
   * exposed. Present on provenance-carrying blocks (health/signals/engine). */
  note?: string
}

/** Never guessed — "unknown" until a real health signal is wired (R0: always "unknown"). */
export type HealthStatus = 'unknown' | 'healthy' | 'degraded' | 'down'

export const HEALTH_STATUS_MAP: Record<HealthStatus, Status> = {
  unknown: 'unknown',
  healthy: 'ok',
  degraded: 'warn',
  down: 'critical',
}

export interface CoverageSummary {
  wired: number
  caveat: number
  gap: number
  total: number
  ratio: number
}

export interface ServiceSummary {
  key: string
  name: string
  archetype?: string
  engine?: string
  owners: string[]
  environments: string[]
  health: HealthStatus
  coverage: CoverageSummary
  badges: string[]
}

export interface ServiceListEnvelope {
  services: ServiceSummary[]
  source: Source
}

export type InventoryStatus = 'wired' | 'caveat' | 'gap'

export interface InventoryItem {
  id: string
  category: string
  categoryLabel?: string
  item: string
  status: InventoryStatus
  rawStatus: string
  evidence: string
}

export interface Lens {
  name: string
  panels?: string[]
}

/** Live backing-engine health (KMS ↔ OpenBao, ADR-0003). Present on the
 * detail only when a service has an engine-health source; reachable stays
 * false with an honest source note when configured-but-down. */
export interface EngineHealth {
  reachable: boolean
  initialized: boolean
  sealed: boolean
  version?: string
  clusterName?: string
  source: Source
}

/** Live KMS key-rotation posture (item 6b), present on the detail only for KMS
 * and only when the operator-token scan is configured. reachable stays false
 * with an honest source note when configured-but-down. Aggregate counts +
 * earliest next rotation only — never key material or names. nextRotationTime
 * is absent when no in-scope key has a schedule; note is set when the scan was
 * bounded (never a silent truncation). */
export interface RotationPosture {
  reachable: boolean
  totalKeys: number
  keysWithRotation: number
  keysWithoutRotation: number
  nextRotationTime?: string
  overdueCount: number
  note?: string
  source: Source
}

export interface ServiceDetail extends ServiceSummary {
  description?: string
  sourceFile?: string
  inventory: InventoryItem[]
  lenses?: Lens[]
  gaps?: string[]
  parseWarnings?: string[]
  engineHealth?: EngineHealth
  rotationPosture?: RotationPosture
}

export interface ServiceDetailEnvelope {
  service: ServiceDetail
  source: Source
}

/** FR-OBS-02 SLO scaffold: a single-window burn-rate approximation, honestly
 * labeled incomplete. target is null when no SLO is configured yet. */
export interface ServiceSLO {
  target: number | null
  burnRateApprox?: number
  note?: string
}

/** Per-service RED golden signals (FR-OBS-01/02) derived from mcd's prom
 * /metrics. gap=true is an honest "no signal", never a guessed zero.
 * errorsUnavailable=true means the signal came from handler-level metrics with
 * no error label — the UI must say so, never render a fake 0% error rate. */
export interface ServiceSignals {
  service: string
  gap: boolean
  windowSeconds: number
  requestsTotal?: number
  requestRatePerSec?: number
  errorRatio?: number
  errorsUnavailable: boolean
  latencyMsAvg?: number
  slo: ServiceSLO
}

export interface ServiceSignalsEnvelope {
  signals: ServiceSignals
  source: Source
}

/** One graphable sample: unix-second timestamp `t` and value `v`. Only finite,
 * real points are ever emitted — NaN/Inf (e.g. an avg over an empty rate
 * window) is skipped upstream, never a fabricated 0. */
export interface RangePoint {
  t: number
  v: number
}

/** One named time series (e.g. "requestRate") with its display unit. `label`
 * is set on the per-verb breakdown series (requestRateByVerb/latencyMsAvgByVerb)
 * — the operation short-name used as a chart legend entry; several series may
 * share `name` and differ only by `label`. `note` carries an honest caveat that
 * travels with the data (e.g. availability is daemon-level, not per-service). */
export interface RangeSeriesOut {
  name: string
  unit: string
  label?: string
  note?: string
  points: RangePoint[]
}

/** Per-service RED signals over a window (range-picker driven), sourced from a
 * real Prometheus range query. gap=true is an honest "no time-series store"
 * (MC_PROM_URL unset) or "store unreachable" — never a fabricated flatline. An
 * empty series with gap=false means recording began recently and fills forward.
 * Mirrors api/admin.v2.yaml ServiceSignalsRange. */
export interface ServiceSignalsRange {
  service: string
  window: '15m' | '1h' | '6h' | '24h' | '7d' | '30d' | 'custom'
  /** The concrete bounds admind queried (unix seconds), always echoed for both
   * presets and custom — the UI deep-links a custom span and derives the
   * compare-overlay shift from these real bounds, never a client guess of now. */
  start: number
  end: number
  gap: boolean
  series: RangeSeriesOut[]
}

export interface ServiceSignalsRangeEnvelope {
  range: ServiceSignalsRange
  source: Source
}

/** Per-service latency percentiles (p50/p95/p99) over a window, computed with
 * histogram_quantile over the native handler_latency_ms_hist_bucket series
 * (ADR-0455). Same gap/empty honesty as ServiceSignalsRange: gap=true is "no
 * store"/"unreachable", an empty series with gap=false is "no traffic in the
 * window". `series` carries three named RangeSeriesOut (latencyP50/P95/P99),
 * unit "ms". Mirrors api/admin.v2.yaml ServiceLatencyRange. */
export interface ServiceLatencyRange {
  service: string
  window: '15m' | '1h' | '6h' | '24h' | '7d' | '30d' | 'custom'
  start: number
  end: number
  gap: boolean
  series: RangeSeriesOut[]
}

export interface ServiceLatencyEnvelope {
  latency: ServiceLatencyRange
  source: Source
}

/** One declared SLO evaluated against the live store. `indicator` is the exact
 * PromQL (for an "open as query" affordance). When `evaluable` is true,
 * value/burnRate/breaching are present; when false, `notEvaluableReason`
 * explains why and the numeric fields are absent — NEVER a fabricated healthy
 * value. Mirrors api/admin.v2.yaml SLOBurnRow. */
export interface SLOBurnRow {
  id: string
  description?: string
  kind: 'latency' | 'ratio'
  indicator: string
  objective: number
  unit?: string
  window?: string
  severity?: string
  pairedAlert?: string
  evaluable: boolean
  value?: number
  burnRate?: number
  breaching?: boolean
  notEvaluableReason?: string
}

/** A service's SLO-burn tiles. gap=true means either no SLO catalog is mirrored
 * for this service, or no time-series store is configured — both honest gaps,
 * distinct from an evaluated SLO that is simply breaching. */
export interface ServiceSLOBurn {
  service: string
  gap: boolean
  slos: SLOBurnRow[]
}

export interface ServiceSLOBurnEnvelope {
  sloBurn: ServiceSLOBurn
  source: Source
}

/** One per-operation (verb) row of the metrics/verbs breakdown table. Every
 * numeric field is optional — a piece the store doesn't expose for that verb is
 * absent (an honest gap), never a fabricated 0. latencyAvgLifetime=true means
 * the windowed rate() average had no sample and the lifetime sum/count average
 * is shown instead — the UI must say which. Mirrors api/admin.v2.yaml
 * VerbMetricRow. */
export interface VerbMetricRow {
  verb: string
  handler: string
  requestsTotal?: number
  ratePerSec?: number
  latencyMsAvg?: number
  latencyAvgLifetime: boolean
  latencyMsMin?: number
  latencyMsMax?: number
}

/** The per-service per-operation breakdown table, sourced from real Prometheus
 * instant queries joined by handler label. gap=true is an honest "no time-series
 * store" (MC_PROM_URL unset) or "store unreachable" — rows is then empty, never
 * fabricated. Mirrors api/admin.v2.yaml ServiceVerbMetricsEnvelope. */
export interface ServiceVerbMetricsEnvelope {
  service: string
  gap: boolean
  rows: VerbMetricRow[]
  source: Source
}

/** One live service tile on the fleet wall (Founder directive): catalog
 * identity, honesty-labeled health, two downsampled real sparklines (rate + avg
 * latency, ≤30 points each), the requests-in-window count, the latest avg
 * latency, and hasSignal. A catalog service with NO series in the store is still
 * present with hasSignal=false and empty sparklines — absence is information,
 * never hidden. latencyMsAvgNow is absent when the service has no latency point
 * in the window (an honest absence, never a fabricated 0). Mirrors
 * api/admin.v2.yaml FleetServiceTile. */
export interface FleetServiceTile {
  key: string
  name: string
  health: HealthStatus
  healthSource: Source
  rateSparkline: RangePoint[]
  latencySparkline: RangePoint[]
  requestsInWindow: number
  latencyMsAvgNow?: number
  hasSignal: boolean
}

/** The fleet-wall summary strip: health counts, how many tiles carry a real
 * signal, total requests across the fleet in the window, and the busiest service
 * key. All derived from the tiles, all real. Mirrors api/admin.v2.yaml
 * FleetTotals. */
export interface FleetTotals {
  servicesTotal: number
  servicesUp: number
  servicesDegraded: number
  servicesDown: number
  servicesUnknown: number
  servicesWithSignal: number
  totalRequestsInWindow: number
  busiestService?: string
}

/** The whole fleet wall: the served window + concrete bounds, an honest gap flag
 * (MC_PROM_URL unset or store unreachable → tiles present with health only,
 * empty sparklines, hasSignal:false — never a fabricated flatline), every
 * visible service as a tile, the totals, and unmappedLabels — svc label values
 * the store carried that map to no catalog service, surfaced honestly rather
 * than silently dropped. Mirrors api/admin.v2.yaml Fleet. */
export interface Fleet {
  window: '15m' | '1h' | '6h' | '24h' | '7d' | '30d'
  start: number
  end: number
  gap: boolean
  services: FleetServiceTile[]
  totals: FleetTotals
  unmappedLabels: string[]
}

export interface FleetEnvelope {
  fleet: Fleet
  source: Source
}

/** The Explore-dock custom-query result — the echoed query + served window, an
 * honest gap flag, and the same RangeSeriesOut shape the per-service graphs use
 * (each series named by its compact label-set, e.g. {handler="server.kmsEncrypt"}).
 * gap=true is an honest failure (bad PromQL / unreachable store); series is then
 * empty and the envelope's source.note carries Prometheus's verbatim error.
 * Mirrors api/admin.v2.yaml MetricsQueryResult. */
export interface MetricsQueryResult {
  query: string
  window: '15m' | '1h' | '6h' | '24h' | '7d' | '30d' | 'custom'
  /** The concrete bounds admind queried (unix seconds), always echoed. */
  start: number
  end: number
  gap: boolean
  series: RangeSeriesOut[]
}

export interface MetricsQueryRangeEnvelope {
  result: MetricsQueryResult
  source: Source
}

/** Every metric NAME recorded in the store (Explore-dock catalog). names is
 * empty with source.kind "none"/"prom-range" when unconfigured/unreachable — an
 * honest gap, never a fabricated list. Mirrors api/admin.v2.yaml
 * MetricsCatalogEnvelope. */
export interface MetricsCatalogEnvelope {
  names: string[]
  count: number
  source: Source
}

/** One metering SKU and its billable operation count (a billing/summary
 * entry). Mirrors api/admin.v2.yaml SkuCount. */
export interface SkuCount {
  sku: string
  count: number
}

/** One derived Medhyai service and its summed billable op count. The service
 * is derived from the SKU by prefix (medhyai.core.<svc> → svc, medhyai.<svc>.*
 * → svc, compute.* → compute, anything else → "other") — a SKU is never
 * dropped, so the mix always sums to the customer total. Mirrors
 * api/admin.v2.yaml ServiceCount. */
export interface ServiceCount {
  service: string
  count: number
}

/** One project's billable-usage row: total billable control-plane ops, top
 * SKUs by volume (≤ 8), and the service mix. Real metering data (the billing
 * pipeline), never a fabricated number. Mirrors api/admin.v2.yaml
 * CustomerUsageRow. */
export interface CustomerUsageRow {
  project: string
  totalOps: number
  topSkus: SkuCount[]
  serviceMix: ServiceCount[]
}

/** The top-customers billable-usage table, sorted by total ops desc. gap=true
 * (customers empty) is an honest "metering not wired" (MC_KMS_API_URL/TOKEN
 * unset) or "unreachable" — never a fabricated customer list. Mirrors
 * api/admin.v2.yaml CustomersUsageEnvelope. */
export interface CustomersUsageEnvelope {
  customers: CustomerUsageRow[]
  gap: boolean
  source: Source
}

/** Per-customer usage-pattern time series: bucketed billable ops over the
 * window (the `ops` total series + one `opsByService` line per busiest
 * service, labeled with the service name), reusing RangeSeriesOut. Zero
 * buckets are REAL metering measurements, never fabricated gaps. gap=true is an
 * honest unreachable/unconfigured metering API. Mirrors api/admin.v2.yaml
 * CustomerUsageRange. */
export interface CustomerUsageRange {
  project: string
  window: '15m' | '1h' | '6h' | '24h' | '7d' | '30d'
  gap: boolean
  series: RangeSeriesOut[]
}

export interface CustomerUsageRangeEnvelope {
  range: CustomerUsageRange
  source: Source
}

export interface CoverageRow {
  service: string
  wired: number
  caveat: number
  gap: number
  total: number
  ratio: number
}

export interface CoverageListEnvelope {
  coverage: CoverageRow[]
  source: Source
}

export type SearchResultKind = 'service' | 'team' | 'person' | 'kb' | 'sop'

export interface SearchResult {
  kind: SearchResultKind
  key: string
  name: string
}

// ---------------------------------------------------------------------------
// Knowledge Base + SOPs (M8: FR-KB-01/02/03).
// ---------------------------------------------------------------------------

export type KBArticleKind = 'operating_manual' | 'architecture' | 'runbook_link'

export interface KBArticle {
  id: string
  kind: KBArticleKind
  title: string
  body?: string
  url?: string
  sourcePath?: string
  updatedAt: string
}

export type SopParamType = 'string' | 'int' | 'bool' | 'duration'

export interface SopParam {
  name: string
  type: SopParamType
  required: boolean
  value?: string
  placeholder?: boolean
}

export interface SopRollback {
  actionId: string
  params?: SopParam[]
}

export interface SopStep {
  order: number
  title: string
  prose: string
  actionId: string
  params?: SopParam[]
  dryRunCapable?: boolean
  rollback?: SopRollback
  note?: string
}

export type SopCell = 'restart' | 'restore_dr' | 'scale' | 'incident_response' | 'security_rotate' | 'other'
export type SopRiskClass = 'low' | 'medium' | 'high'
export type SopLifecycleState = 'draft' | 'certified' | 'drift-flagged'

export interface Sop {
  slug: string
  title: string
  cell: SopCell
  services: string[]
  riskClass: SopRiskClass
  lifecycleState: SopLifecycleState
  ownerTeam: string
  isSeed?: boolean
}

export interface SopDetail extends Sop {
  trigger: string
  preconditions?: string[]
  steps: SopStep[]
  rollback?: string
  verification: string[]
  lastDrillAt?: string
  driftNotes?: string[]
  sourcePaths?: string[]
}

export interface SopListEnvelope {
  sops: Sop[]
  source: Source
}

export interface SopEnvelope {
  sop: SopDetail
  source: Source
}

export interface KBServiceDetail {
  service: string
  articles: KBArticle[]
  sops: Sop[]
}

export interface KBServiceEnvelope {
  kb: KBServiceDetail
  source: Source
}

export type SopCoverageStatus = SopLifecycleState | 'gap'

export interface SopCoverageCell {
  cell: SopCell
  status: SopCoverageStatus
  sopSlug?: string
  sopTitle?: string
}

export interface SopCoverageRow {
  service: string
  cells: SopCoverageCell[]
}

export interface SopCoverageEnvelope {
  coverage: SopCoverageRow[]
  source: Source
}

// ---------------------------------------------------------------------------
// SOP graph engine (AWU sop-graph-engine) — the versioned flowchart-SOP model
// (distinct from the linear Sop/SopDetail above). READ surface is GET-only
// (/sops/graphs, /sops/runs, …); every MUTATION flows through the action
// gateway (sop.version.* / sop.graph.* / sop.run.*) via invokeAction, the one
// mutation choke point. Types mirror internal/sop verbatim so the UI never
// re-derives the engine's shape. Zero AI: an "agent" is purely another
// identity string — the server enforces that an agent may author but never
// approve a version.
// ---------------------------------------------------------------------------

export type SopIdentityKind = 'human' | 'agent'

/** A (kind, id) author / approver / driver of a SOP version or run. */
export interface SopIdentity {
  kind: SopIdentityKind
  id: string
}

export type SopGraphNodeKind = 'step' | 'decision'

/** One labeled outgoing edge from a decision node. Empty `next` is a legal terminal branch. */
export interface SopBranch {
  label: string
  next?: string
}

/**
 * One flowchart node. `step` nodes carry human prose + an OPTIONAL typed
 * gateway action and a single `next` edge; `decision` nodes carry a
 * plain-English question (prose) + >=2 labeled `branches`. A node whose only
 * outgoing edge is empty is terminal.
 */
export interface SopGraphNode {
  id: string
  kind: SopGraphNodeKind
  title?: string
  prose: string
  actionId?: string
  params?: SopParam[]
  dryRunCapable?: boolean
  rollback?: SopRollback
  note?: string
  next?: string
  branches?: SopBranch[]
}

/** A flowchart SOP: one entry node id + the set of nodes. */
export interface SopGraph {
  entry: string
  nodes: SopGraphNode[]
}

/** One authored SOP document (a version's body) — metadata plus EITHER a graph or a linear steps chain. */
export interface SopDoc {
  key: string
  title: string
  cell: SopCell
  services: string[]
  trigger: string
  preconditions?: string[]
  riskClass: SopRiskClass
  verification: string[]
  ownerTeam: string
  steps?: SopStep[]
  graph?: SopGraph
}

export type SopVersionState = 'draft' | 'active' | 'superseded' | 'archived'

export const SOP_VERSION_STATE_STATUS: Record<SopVersionState, Status> = {
  draft: 'unknown',
  active: 'ok',
  superseded: 'info',
  archived: 'warn',
}

export interface SopVersion {
  id: string
  graphKey: string
  version: number
  state: SopVersionState
  title: string
  doc: SopDoc
  author: SopIdentity
  approvedBy?: SopIdentity
  approvedAt?: string
  createdAt: string
  updatedAt: string
}

export interface SopGraphSummary {
  key: string
  title: string
  archived: boolean
  activeVersion: number
  latestVersion: number
  createdAt: string
  archivedAt?: string
}

/** One authoring-history transition (draft/approve/supersede/archive) with its actor + reason. */
export interface SopVersionEvent {
  transition: string
  actor: SopIdentity
  reason?: string
  at: string
}

export interface SopGraphListEnvelope {
  graphs: SopGraphSummary[]
  source: Source
}

export interface SopGraphDetailEnvelope {
  key: string
  versions: SopVersion[]
  history: SopVersionEvent[]
  activeVersion?: SopVersion
  activeGraph?: SopGraph
  source: Source
}

export interface SopGraphVersionEnvelope {
  version: SopVersion
  graph: SopGraph
  source: Source
}

/** Deterministic per-node quality aggregate across a family's runs (the SOP-quality board). */
export interface SopNodeAnalytics {
  nodeId: string
  kind: string
  runsIncluding: number
  entered: number
  done: number
  skipped: number
  blocked: number
  failed: number
  comments: number
  notWorking: number
  failureRate: number
  skipRate: number
}

export interface SopAnalyticsEnvelope {
  graphKey: string
  nodes: SopNodeAnalytics[]
  source: Source
}

export type SopRunStatus = 'active' | 'completed' | 'abandoned'

export const SOP_RUN_STATUS_STATUS: Record<SopRunStatus, Status> = {
  active: 'info',
  completed: 'ok',
  abandoned: 'warn',
}

export type SopRunNodeStatus = 'pending' | 'in_progress' | 'done' | 'skipped' | 'blocked' | 'failed'

export const SOP_RUN_NODE_STATUS_STATUS: Record<SopRunNodeStatus, Status> = {
  pending: 'unknown',
  in_progress: 'info',
  done: 'ok',
  skipped: 'warn',
  blocked: 'warn',
  failed: 'critical',
}

export interface SopRun {
  id: string
  graphKey: string
  version: number
  versionId: string
  status: SopRunStatus
  driver: SopIdentity
  triggerRef?: string
  currentNodeId?: string
  rev: number
  startedBy: SopIdentity
  startedAt: string
  updatedAt: string
  completedAt?: string
}

export interface SopRunNode {
  nodeId: string
  kind: string
  status: SopRunNodeStatus
  chosenBranch?: string
  actionExecutionId?: string
  actionOutcome?: string
  enteredAt?: string
  completedAt?: string
}

export interface SopRunComment {
  nodeId: string
  author: SopIdentity
  body: string
  notWorking: boolean
  createdAt: string
}

export interface SopRunEvent {
  event: string
  nodeId?: string
  actor: SopIdentity
  detail?: unknown
  at: string
}

export interface SopRunListEnvelope {
  runs: SopRun[]
  source: Source
}

export interface SopRunDetailEnvelope {
  run: SopRun
  nodes: SopRunNode[]
  comments: SopRunComment[]
  timeline: SopRunEvent[]
  source: Source
}

/** The tick outcomes a run node can conclude with (done/skipped advance; blocked/failed park). */
export type SopTickOutcome = 'done' | 'skipped' | 'blocked' | 'failed'

export interface SearchResultListEnvelope {
  results: SearchResult[]
  source: Source
}

export interface Meta {
  name: string
  version: string
  /** Whether MDI_ADMIN_URL/MDI_ADMIN_TOKEN are set (ADR-0004): false means service.drain/service.undrain honestly refuse as not wired, and security.rotate falls back to its portal-side-record note instead of calling mcd's real rotate endpoint. */
  mcdAdminWired: boolean
}

// ---------------------------------------------------------------------------
// Alerting Management (M3: FR-ALR-01/02/03).
// ---------------------------------------------------------------------------

export type AlertSeverity = 'page' | 'warn' | 'info'
export type AlertSourceKind = 'rule' | 'burn' | 'anomaly' | 'manual'
export type AlertStatus = 'firing' | 'acked' | 'silenced' | 'resolved'

/** Severity -> the design system's status scale (docs/design/00-design-system.md). */
export const ALERT_SEVERITY_STATUS_MAP: Record<AlertSeverity, Status> = {
  page: 'critical',
  warn: 'warn',
  info: 'info',
}

/** Status -> the design system's status scale, for the lifecycle chip. */
export const ALERT_STATUS_MAP: Record<AlertStatus, Status> = {
  firing: 'critical',
  acked: 'warn',
  silenced: 'unknown',
  resolved: 'ok',
}

export interface Alert {
  id: string
  service: string
  severity: AlertSeverity
  title: string
  note?: string
  source: AlertSourceKind
  ruleKey?: string
  status: AlertStatus
  owningTeam?: string
  escalationTier: number
  assignedOnCallKind?: 'person' | 'agent'
  assignedOnCallId?: string
  runbookUrl?: string
  sopSlug?: string
  hasRunbook: boolean
  correlationGroup: string
  occurrenceCount: number
  lastEscalatedAt?: string
  ackedBy?: string
  ackedAt?: string
  silenceReason?: string
  silencedBy?: string
  silencedAt?: string
  silencedUntil?: string
  resolvedBy?: string
  resolvedAt?: string
  resolutionKind?: 'auto' | 'manual'
  createdAt: string
  updatedAt: string
}

export interface AlertFilter {
  service?: string
  team?: string
  severity?: AlertSeverity
  status?: AlertStatus
}

export interface AlertListEnvelope {
  alerts: Alert[]
  source: Source
}

export interface AlertEnvelope {
  alert: Alert
  source: Source
}

export interface AlertQualityRow {
  team: string
  total: number
  noiseRatio: number
  actionability: number
  ackLatencySecondsAvg?: number
  note?: string
}

export interface AlertQualityEnvelope {
  quality: AlertQualityRow[]
  source: Source
}

export interface Silence {
  alertId: string
  service: string
  severity: AlertSeverity
  title: string
  reason: string
  silencedBy: string
  silencedAt: string
  silencedUntil: string
}

export interface SilenceListEnvelope {
  silences: Silence[]
  source: Source
}

/** One per-recipient in-portal notification (the notification/escalation spine). */
export interface Notification {
  id: string
  escalationId: string
  alertId: string
  service: string
  teamKey?: string
  level: number
  severity: AlertSeverity
  title: string
  /** 'digest' = digest-queued honestly; the digest delivery lane is a later phase. */
  lane: 'page' | 'digest'
  read: boolean
  readAt?: string
  createdAt: string
}

export interface NotificationListEnvelope {
  notifications: Notification[]
  /** Unread page-lane notifications for the badge (digest-queued rows excluded). */
  unreadCount: number
  source: Source
}

// ---------------------------------------------------------------------------
// Org (M0: FR-ORG-01) — just enough to pick a real commander/responder for
// the Incidents module; the full org/RBAC management UI is a later AWU.
// ---------------------------------------------------------------------------

export interface OrgPerson {
  id: string
  name: string
  email: string
  active: boolean
}

export interface OrgAgent {
  id: string
  name: string
  kind: string
  description?: string
  active: boolean
}

export interface OrgRole {
  id: string
  key: string
  name: string
  description?: string
}

export interface OrgTeam {
  id: string
  key: string
  name: string
  description?: string
  provisional: boolean
}

export interface OrgPeopleEnvelope {
  people: OrgPerson[]
  source: Source
}

export interface OrgAgentsEnvelope {
  agents: OrgAgent[]
  source: Source
}

export interface OrgRolesEnvelope {
  roles: OrgRole[]
  source: Source
}

export interface OrgTeamsEnvelope {
  teams: OrgTeam[]
  source: Source
}

// ---------------------------------------------------------------------------
// Incidents & Emergency Ops (M4: FR-INC-01/02/03).
// ---------------------------------------------------------------------------

export type IncidentSeverity = 'SEV1' | 'SEV2' | 'SEV3' | 'SEV4'
export type IncidentStatus = 'declared' | 'mitigating' | 'monitoring' | 'resolved'
export type StatuspageState = 'investigating' | 'identified' | 'monitoring' | 'resolved'
export type IncidentMemberKind = 'person' | 'agent'

/** Severity -> the design system's status scale — SEV1/2 page-worthy, SEV3/4 lower. */
export const INCIDENT_SEVERITY_STATUS_MAP: Record<IncidentSeverity, Status> = {
  SEV1: 'critical',
  SEV2: 'critical',
  SEV3: 'warn',
  SEV4: 'info',
}

/** Status -> the design system's status scale, for the lifecycle chip. */
export const INCIDENT_STATUS_MAP: Record<IncidentStatus, Status> = {
  declared: 'critical',
  mitigating: 'warn',
  monitoring: 'info',
  resolved: 'ok',
}

export interface IncidentResponder {
  kind: IncidentMemberKind
  id: string
  addedAt: string
}

export interface Incident {
  id: string
  title: string
  severity: IncidentSeverity
  status: IncidentStatus
  commanderKind?: IncidentMemberKind
  commanderId?: string
  responders: IncidentResponder[]
  affectedServices: string[]
  linkedAlertIds: string[]
  statuspageState: StatuspageState
  declaredByKind: string
  declaredById: string
  declaredAt: string
  resolvedAt?: string
  commsSlaDeadline?: string
  commsSlaBreached: boolean
  createdAt: string
  updatedAt: string
}

export interface IncidentFilter {
  status?: IncidentStatus
  severity?: IncidentSeverity
  service?: string
}

export interface IncidentListEnvelope {
  incidents: Incident[]
  source: Source
}

export type TimelineKind =
  | 'declared'
  | 'status_changed'
  | 'severity_changed'
  | 'commander_changed'
  | 'responder_added'
  | 'alert_linked'
  | 'comms_posted'
  | 'pir_opened'
  | 'action_executed'

export interface TimelineEntry {
  id: string
  occurredAt: string
  kind: TimelineKind
  actor?: string
  summary: string
  detail?: Record<string, unknown>
}

export interface TimelineEnvelope {
  timeline: TimelineEntry[]
  source: Source
}

export interface PIRActionItem {
  id: string
  description: string
  sopSlug?: string
  createdAt: string
}

export type PIRStatus = 'draft' | 'submitted'

export interface PIR {
  id: string
  incidentId: string
  status: PIRStatus
  summary: string
  rootCause: string
  impact: string
  actionItems: PIRActionItem[]
  openedBy: string
  openedAt: string
  submittedAt?: string
  updatedAt: string
}

export interface ServiceHealthView {
  service: string
  health: HealthStatus
  source: Source
}

export interface IncidentDetail extends Incident {
  timeline: TimelineEntry[]
  pir?: PIR
  linkedAlertsDetail: Alert[]
  affectedServiceHealth: ServiceHealthView[]
}

export interface IncidentDetailEnvelope {
  incident: IncidentDetail
  source: Source
}

// ---------------------------------------------------------------------------
// Customer Insight (M5: FR-CUS-01/02/03) & Support Workspace (M6: FR-SUP-01/
// 02/03), ADR-0005 R2: tenant data sourced from mcd's admin-plane tenant
// surface via internal/mcdadmin — real tenant data, never sample/fixture
// data (same posture as terminals/security). `wired`/`source.kind` tell you
// when MDI_ADMIN_URL/MDI_ADMIN_TOKEN aren't configured — an honest gap, not
// a fabricated zero-usage tenant.
// ---------------------------------------------------------------------------

export interface FootprintResource {
  kind: string
  count: number
}

export interface ServiceFootprint {
  service: string
  resources: FootprintResource[]
}

export interface ServiceUsage {
  service: string
  metric: string
  value: number
  unit: string
}

/** One row of mcd's admin-plane tenant registry (ADR-0005), decoded from its PINNED wire shape exactly. */
export interface Tenant {
  id: string
  displayName: string
  state: string
  /** null when mcd reports a zero-time (a tenant with no recorded create/activity time — honest gap, never 0001-01-01). */
  createTime: string | null
  serviceCount: number
  /** null when mcd reports a zero-time — the console renders this as "never", not a computed relative age. */
  lastActivityAt: string | null
}

export interface TenantListEnvelope {
  tenants: Tenant[]
  source: Source
}

/** A slim incident reference on the tenant-360 view — the full war-room detail lives at /incidents/:id. */
export interface IncidentRef {
  id: string
  title: string
  severity: IncidentSeverity
  status: IncidentStatus
  declaredAt: string
}

export interface TenantDetail extends Tenant {
  /** False when MDI_ADMIN_URL/MDI_ADMIN_TOKEN are unset — every field below is then an honest empty gap. */
  wired: boolean
  footprint: ServiceFootprint[]
  usage: ServiceUsage[]
  openIncidents: IncidentRef[]
}

export interface TenantDetailEnvelope {
  tenant: TenantDetail
  source: Source
}

export interface ServiceTenantUsage {
  metric: string
  value: number
  unit: string
}

/** One row of GET /services/{svc}/customers — a tenant using that service (FR-CUS-01, the M5 pivot). */
export interface ServiceCustomer {
  tenant: string
  displayName: string
  usage: ServiceTenantUsage
}

export interface ServiceCustomersEnvelope {
  service: string
  tenants: ServiceCustomer[]
  source: Source
}

// ---------------------------------------------------------------------------
// Action gateway (FR-PLT-05) — the single mutation choke point every
// alert.*/sop.*/etc invocation flows through.
// ---------------------------------------------------------------------------

export type ExecutionStatus = 'pending' | 'pending_approval' | 'succeeded' | 'failed' | 'refused'

export interface Execution {
  execution_id: string
  actionId: string
  status: ExecutionStatus
  audit_ref: string
  result?: unknown
  refusalReason?: string
  idempotencyKey: string
  createdAt: string
  updatedAt: string
}

// ---------------------------------------------------------------------------
// Developer Workspace: terminals (M7 FR-DEV-01) — the Access lane. Opening
// and killing a session are gateway actions (terminal.open/terminal.kill via
// invokeAction); every other terminal.* shape here is read-only.
// ---------------------------------------------------------------------------

export type TerminalTargetKind = 'local-sandbox' | 'ssh'
export type TerminalSessionStatus = 'live' | 'closed' | 'expired' | 'killed'

/** Status -> the design system's status scale, for the session lifecycle chip. */
export const TERMINAL_STATUS_MAP: Record<TerminalSessionStatus, Status> = {
  live: 'ok',
  closed: 'info',
  expired: 'warn',
  killed: 'critical',
}

export interface TerminalSession {
  id: string
  targetKey: string
  targetKind: TerminalTargetKind
  targetLabel: string
  environmentKey?: string
  openedByKind: string
  openedById: string
  reason: string
  status: TerminalSessionStatus
  openedAt: string
  expiresAt: string
  idleTimeoutSeconds: number
  lastActivityAt: string
  closedAt?: string
  closedReason?: string
  closedByKind?: string
  closedById?: string
  openExecutionId: string
  openAuditRef?: string
}

export interface SSHOutsideReport {
  configured: boolean
  note: string
  count?: number
}

export interface TerminalFilter {
  target?: string
  status?: TerminalSessionStatus
}

export interface TerminalSessionListEnvelope {
  sessions: TerminalSession[]
  sshOutsidePortal: SSHOutsideReport
  source: Source
}

export interface TerminalSessionEnvelope {
  session: TerminalSession
  isLive: boolean
  source: Source
}

export type TerminalRecordingDirection = 'input' | 'output'

export interface TerminalRecordingEvent {
  sessionId: string
  seq: number
  direction: TerminalRecordingDirection
  /** Base64-encoded recorded bytes. */
  data: string
  occurredAt: string
  prevHash: string
  hash: string
}

export interface TerminalRecordingEnvelope {
  sessionId: string
  events: TerminalRecordingEvent[]
  tamperEvident: boolean
  verifyNote: string
  source: Source
}

// ---------------------------------------------------------------------------
// Security Center R1 management surfaces (M12 R1: FR-SEC-03/04/05/06).
// Rotate/recertify/revoke/mark-patched/register/drill/posture-update are all
// gateway actions (security.* via invokeAction); every shape here is
// read-only. No fixtures by design (same posture as terminals): this is
// live security-governance state, never presented as sample data.
// ---------------------------------------------------------------------------

export type RotationResourceKind = 'key' | 'cert' | 'secret'

export interface RotationTarget {
  id: string
  kind: RotationResourceKind
  name: string
  serviceKey: string
  rotationPeriodDays: number
  lastRotatedAt: string
  expiresAt: string
  createdAt: string
}

export interface RotationListEnvelope {
  rotations: RotationTarget[]
  source: Source
}

export type VulnSeverity = 'critical' | 'high' | 'medium' | 'low'
export type VulnStatus = 'open' | 'patching' | 'patched' | 'accepted-risk'

export interface VulnFinding {
  id: string
  serviceKey: string
  cveId: string
  engine?: string
  severity: VulnSeverity
  status: VulnStatus
  discoveredAt: string
  slaDays: number
  slaDeadline: string
  slaBreached: boolean
  patchedAt?: string
  source: string
  notes?: string
  createdAt: string
}

export interface VulnListEnvelope {
  vulns: VulnFinding[]
  source: Source
}

export interface AccessReviewCampaign {
  id: string
  name: string
  openedAt: string
  closesAt: string
  status: 'open' | 'closed'
  openedByKind: string
  openedById: string
  createdAt: string
  totalItems: number
  pendingItems: number
  recertifiedItems: number
  revokedItems: number
}

export interface AccessReviewCampaignListEnvelope {
  campaigns: AccessReviewCampaign[]
  source: Source
}

export interface DormancyReport {
  tracked: boolean
  note: string
}

export type AutonomyGrade = 'A0' | 'A1' | 'A2' | 'A3'

export interface AccessReviewItem {
  id: string
  campaignId: string
  grantId: string
  subjectKind: 'person' | 'agent'
  subjectId: string
  subjectName: string
  roleKey: string
  teamKey: string
  serviceKey?: string
  environmentKey?: string
  grantKind: 'see' | 'do'
  autonomyGrade?: AutonomyGrade
  status: 'pending' | 'recertified' | 'revoked'
  decidedAt?: string
  decidedByKind?: string
  decidedById?: string
  decisionReason?: string
  dormancy: DormancyReport
  createdAt: string
}

export interface AccessReviewCampaignDetailEnvelope {
  campaign: AccessReviewCampaign
  items: AccessReviewItem[]
  source: Source
}

export interface BreakGlassDrill {
  id: string
  registrationId: string
  conductedAt: string
  conductedByKind: string
  conductedById: string
  outcome: 'pass' | 'fail'
  notes?: string
}

export interface BreakGlassRegistration {
  id: string
  name: string
  description?: string
  holderKind: string
  holderId: string
  holderName: string
  serviceKey?: string
  status: 'active' | 'retired'
  createdAt: string
  drills: BreakGlassDrill[]
}

export interface BreakGlassListEnvelope {
  registrations: BreakGlassRegistration[]
  source: Source
}

export interface TerminalReviewItem {
  sessionId: string
  session: TerminalSession
  status: 'pending' | 'reviewed'
  reviewedAt?: string
  reviewedByKind?: string
  reviewedById?: string
  note?: string
}

export interface TerminalReviewListEnvelope {
  reviews: TerminalReviewItem[]
  source: Source
}

// ---------------------------------------------------------------------------
// Auth (r0-authn, ADR-0002 D5) — WebAuthn ceremonies, session, persona.
// ---------------------------------------------------------------------------

export type PrincipalKind = 'person' | 'dev-token'

export interface Principal {
  kind: PrincipalKind
  email?: string
  name?: string
  roles: string[]
  teams: string[]
  seesAllServices: boolean
}

export interface WhoAmIEnvelope {
  principal: Principal
  source?: Source
}

/** A WebAuthn options challenge plus the ceremony id to echo back on /finish. */
export interface CeremonyEnvelope {
  publicKey: object
  ceremonyId: string
}

// ---------------------------------------------------------------------------
// Home dashboard — sample data only (no /admin/v2 endpoint backs these yet;
// see FIXTURES_ONLY widgets in components/widgets/home). Kept separate from
// the live catalog/coverage/search types above.
// ---------------------------------------------------------------------------

export interface Kpi {
  label: string
  value: number | null
  unit?: string
  delta?: { value: number; label: string; tone: 'good' | 'bad' | 'neutral' }
  spark?: number[]
  provenance: Provenance
  /** true when there is no source at all yet (data-truth rule #2) — distinct from stale. */
  unknown?: boolean
}

export type JourneyStatus = 'pass' | 'degraded' | 'fail'

export interface Journey {
  name: string
  status: JourneyStatus
  lastRun: string
  services: string
  history?: boolean[]
}

/** The Home dashboard's leadership KPI-widget shape — sample data only, distinct from the real Incident resource (M4) below. */
export interface SampleIncident {
  id: string
  severity: 'sev1' | 'sev2' | 'sev3'
  service: string
  description: string
  duration: string
  owner: string
}

export interface SecurityCategory {
  name: string
  pct: number
}

export interface TenantMovement {
  name: string
  tier: string
  deltaPct: number
  mrrLabel: string
}

/**
 * The CEO Home dashboard's active-incident posture — REAL, composed from
 * GET /admin/v2/leadership/summary (leadership-composed). Distinct from the
 * per-incident Incident resource (M4): the summary carries counts, not rows.
 */
export interface HomeIncidentsPosture {
  total: number
  bySeverity: Record<string, number>
}

/**
 * The CEO Home dashboard model. Post real-integration-1 this is composed
 * from GET /admin/v2/leadership/summary — `kpis`, `incidents`, and
 * `security` carry REAL numbers (each with honest provenance); `journeys`
 * and `tenants` have no backing feed yet and are always empty, rendered as
 * an honest "not wired yet" gap (never fabricated rows). See
 * api/client.ts fetchHomeDashboard.
 */
export interface HomeDashboardData {
  kpis: Kpi[]
  /** Always empty — no synthetic-journeys endpoint exists; the widget renders a labeled gap. */
  journeys: Journey[]
  incidents: HomeIncidentsPosture
  security: SecurityCategory[]
  /** Always empty — no billing tenant-movements feed exists; the widget renders a labeled gap. */
  tenants: TenantMovement[]
  provenance: {
    journeys: Provenance
    incidents: Provenance
    security: Provenance
    tenants: Provenance
  }
}

// ---------------------------------------------------------------------------
// Leadership Command Center (M11: FR-LEAD-01/02/03) — real, composed data;
// distinct from the Home dashboard's sample-data widgets above.
// ---------------------------------------------------------------------------

export interface FleetHealthTile {
  total: number
  healthy: number
  degraded: number
  down: number
  unknown: number
  drillHref: string
  source: Source
}

export interface SLOPostureTile {
  meeting: number
  breaching: number
  notConfigured: number
  drillHref: string
  source: Source
}

export interface IncidentsPostureTile {
  total: number
  bySeverity: Record<string, number>
  drillHref: string
  source: Source
}

export interface AlertsPostureTile {
  openPage: number
  openTotal: number
  drillHref: string
  source: Source
}

export interface UsageMetric {
  metric: string
  unit: string
  value: number
}

export interface CustomerRealityTile {
  wired: boolean
  tenantCount: number
  usage: UsageMetric[]
  drillHref: string
  source: Source
}

export interface ObservabilityPostureTile {
  fleetCoveragePct: number
  servicesRanked: number
  worstService?: string
  worstServiceRatio: number
  drillHref: string
  source: Source
}

export interface SecurityPostureTile {
  overdueRotations: number
  totalRotations: number
  slaBreachedVulns: number
  totalVulns: number
  drillHref: string
  lineage: string
  source: Source
}

export interface TeamThroughputRow {
  teamKey: string
  teamName: string
  ownedServiceCount: number
  openIncidents: number
  openAlerts: number
  alertActionability?: number
}

export interface TeamThroughputTile {
  rows: TeamThroughputRow[]
  drillHref: string
  lineage: string
  source: Source
}

export interface HonestGapTile {
  label: string
  reason: string
  drillHref: string
}

export interface LeadershipSummary {
  generatedAt: string
  fleetHealth: FleetHealthTile
  slo: SLOPostureTile
  incidents: IncidentsPostureTile
  alerts: AlertsPostureTile
  customers: CustomerRealityTile
  observability: ObservabilityPostureTile
  security: SecurityPostureTile
  teamThroughput: TeamThroughputTile
  agentPerformance: HonestGapTile
  spendMargin: HonestGapTile
}

export interface LeadershipSummaryEnvelope {
  summary: LeadershipSummary
  source: Source
}

// ---------------------------------------------------------------------------
// Pricing module (P2: docs/design/pricing-and-discounts.md §4). Reads are
// fleet-wide-only (like Security governance) and composed from mcd's
// billing admin surface via internal/pricing. Every mutation
// (pricing.config.update/pricing.pricebook.update/pricing.credit.adjust)
// flows through the action gateway (invokeAction) — no separate write path.
// All money is microdollar ints, all fractions are ppm ints — never floats
// on the wire (§1).
// ---------------------------------------------------------------------------

export interface PricingConfig {
  gcpDiscountMultiplierPpm: number
  newUserCreditMicros: number
  creditCoverFractionPpm: number
  newUserCreditEnabled: boolean
  version: number
  updatedBy?: string
}

export interface PricingConfigEnvelope {
  config: PricingConfig
  source: Source
}

export interface PricebookEntry {
  skuId: string
  serviceId: string
  description: string
  unit: string
  gcpListRateMicros: number
  multiplierPpm?: number | null
  effectiveRateMicros: number
  source?: string
  updatedBy?: string
  updatedAt?: string
}

export interface PricebookListEnvelope {
  entries: PricebookEntry[]
  source: Source
}

/**
 * RackBookEntry is one row of the rack book — the book mcd's settlement
 * engine ACTUALLY rates usage against (ADR-0470), as opposed to
 * PricebookEntry above (the legacy customer-facing rate card settlement
 * never reads). `sku` here is the emitted METER namespace
 * ("compute.snapshot"), never the GCP list-price id ("pd-snapshot")
 * PricebookEntry uses — the two namespaces share NO ids by construction,
 * which is the whole reason both panels exist side by side.
 */
export interface RackBookEntry {
  sku: string
  service: string
  unit: string
  quantumExp: number
  /** true when this SKU has NEVER been priced — every usage record against it holds. */
  held: boolean
  /** true when the shown rate covers "now". held=false && active=false means a forward-only first rate is scheduled but hasn't started. */
  active: boolean
  listMicrosUsd?: number
  valueNanoBu?: number
  effectiveFrom?: string
  effectiveTo?: string
  reason?: string
}

export interface RackBookListEnvelope {
  entries: RackBookEntry[]
  source: Source
}

export interface CreditGrant {
  accountId: string
  amount: number
  reason: string
  actor: string
  at?: string
}

export interface AccountCredit {
  accountId: string
  displayName: string
  balanceMicros: number
  grants: CreditGrant[]
}

export interface AccountCreditListEnvelope {
  accounts: AccountCredit[]
  source: Source
}

// ---------------------------------------------------------------------------
// Infrastructure Fleet (M13 S1/S2, docs/prd/03: Fleet board / Cell page /
// Node page) — cell/node lifecycle read views. Real data, no fixtures (same
// posture as terminals/security/pricing): every cell/node is live fleet
// state, never sample data. Distinct from the existing Fleet.tsx (the M2
// service catalog) — see nav-items.ts's naming guardrail.
// ---------------------------------------------------------------------------

export type CellState = 'provisioning' | 'active' | 'retired'

export const CELL_STATE_STATUS_MAP: Record<CellState, Status> = {
  provisioning: 'info',
  active: 'ok',
  retired: 'unknown',
}

/**
 * A demo cell's billing clock (SOP-007, M13 S5). Present only on demo cells
 * (ordinary cells omit it). The client ticks billingStartedAt→now itself for
 * a running clock; finalMinutes is the settled cost-minutes, sent only once
 * the demo has ended (billingStoppedAt set).
 */
export interface DemoCellMeta {
  billingStartedAt: string
  billingStoppedAt?: string
  provider: string
  ratePerMinuteNote?: string
  /** true when the compute was never really provisioned (the StubProvisioner ran) — the UI must not claim "live". */
  stub: boolean
  finalMinutes?: number
  endNote?: string
}

export interface CellSummary {
  id: string
  name: string
  provider: string
  shape: string
  state: CellState
  nodeCount: number
  createdAt: string
  demo?: DemoCellMeta
}

/** The 00-PROGRAM.md node lifecycle (M13 S1, internal/fleet/state.go), verbatim. */
export type NodeState =
  | 'ORDERED'
  | 'PROVISIONED'
  | 'BURN_IN'
  | 'HARDENED'
  | 'REGISTERED'
  | 'READY'
  | 'IN_SERVICE'
  | 'CORDONED'
  | 'DRAINING'
  | 'MAINTENANCE'
  | 'PROBATION'
  | 'REPAIR'
  | 'DEBOARDING'
  | 'SANITIZED'
  | 'RETURNED'

export const NODE_STATE_STATUS_MAP: Record<NodeState, Status> = {
  ORDERED: 'unknown',
  PROVISIONED: 'info',
  BURN_IN: 'info',
  HARDENED: 'info',
  REGISTERED: 'info',
  READY: 'info',
  IN_SERVICE: 'ok',
  CORDONED: 'warn',
  DRAINING: 'warn',
  MAINTENANCE: 'warn',
  PROBATION: 'critical',
  REPAIR: 'critical',
  DEBOARDING: 'unknown',
  SANITIZED: 'unknown',
  RETURNED: 'unknown',
}

export interface NodeSummary {
  id: string
  cellId: string
  name: string
  role: string
  state: NodeState
  providerRef?: string
  createdAt: string
  updatedAt: string
}

export interface InfrastructureCellListEnvelope {
  cells: CellSummary[]
  gaps: HonestGapTile[]
  source: Source
}

export interface InfrastructureCellDetailEnvelope {
  cell: CellSummary
  nodes: NodeSummary[]
  source: Source
}

/** One immutable node_lifecycle_events row (M13 S1) — every transition is an audited action. */
export interface LifecycleEvent {
  id: string
  nodeId: string
  fromState?: NodeState | ''
  toState: NodeState
  actorKind: string
  actorId: string
  actionId: string
  at: string
}

export interface InfrastructureNodeDetailEnvelope {
  node: NodeSummary
  history: LifecycleEvent[]
  /** Honest gap: no fleet-telemetry pipeline exists yet — vitals ship with the node-agent (M13 S3+). */
  vitals: HonestGapTile
  source: Source
}

// ---------------------------------------------------------------------------
// M14 Autopilot Command (docs/prd/03 D8) — the separate cockpit. Composed
// read-only from the grade sheet (11-autonomy-grades.md), agent grants, the
// gateway execution log, and the freeze state. The one mutation the dashboard
// drives, autopilot.scope.set_mode, flows through invokeAction like every
// other action; the freeze button invokes system.freeze/system.unfreeze.
// ---------------------------------------------------------------------------

/** The autonomy-ladder position (11-autonomy-grades.md). */
export type AutopilotGrade = 'A0' | 'A1' | 'A2' | 'A3'

/** The three-level operator label the scope map renders from a grade. */
export type AutopilotMode = 'HUMAN' | 'AUTO+NOTIFY' | 'AUTONOMOUS'

export interface AutopilotGradeSheetRow {
  id: string
  label: string
  sop: string
  gradeNow: AutopilotGrade
  ceiling: AutopilotGrade
  grade: AutopilotGrade
  mode: AutopilotMode
  /** A data-destructive/data-adjacent class (law 2) — permanent lock, no toggle. */
  a3Never: boolean
  overridden: boolean
  rationale: string
}

export interface AutopilotScopeCell {
  classId: string
  grade: AutopilotGrade
  mode: AutopilotMode
  ceiling: AutopilotGrade
  a3Never: boolean
  overridden: boolean
}

export interface AutopilotScopeRow {
  serviceKey: string
  serviceName: string
  cells: AutopilotScopeCell[]
}

export interface AutopilotAgentGrant {
  agentId: string
  agentName: string
  serviceKey: string
  grade: AutopilotGrade
  mode: AutopilotMode
}

export interface AutopilotFreeze {
  frozen: boolean
  reason?: string
}

export interface AutopilotCounts {
  services: number
  classes: number
  inbox: number
  feed: number
  agentsUnderAutopilot: number
}

export interface AutopilotOverview {
  generatedAt: string
  gradeSheet: AutopilotGradeSheetRow[]
  classOrder: string[]
  scope: AutopilotScopeRow[]
  freeze: AutopilotFreeze
  agents: AutopilotAgentGrant[]
  counts: AutopilotCounts
  source: Source
}

export interface AutopilotExecution {
  executionId: string
  actionId: string
  actorKind: string
  actorId: string
  status: ExecutionStatus
  riskClass: string
  refusalReason?: string
  auditRef?: string
  createdAt: string
  updatedAt: string
}

export interface AutopilotExecutionsEnvelope {
  executions: AutopilotExecution[]
  source: Source
}

/**
 * Studio agent-backend config — the source of truth is the Medhyai Studio
 * daemon's agent-config.json (GET/PUT /agent-config, dev-proxied at
 * /studio-agent). The Studio driver re-reads it every turn, so saves apply to
 * the next agent turn without a restart. Shape mirrors that file 1:1.
 */
export interface StudioAgentConfig {
  mode: 'cli' | 'openai'
  cli: { bin: string; mainModel: string; mainEffort: string; watcherModel: string; watcherEffort: string }
  openai: { baseUrl: string; apiKey: string; mainModel: string; watcherModel: string }
  watcher: { enabled: boolean; maxFixRounds: number }
}
